import { Binder } from '@ribajs/core'
import { waitForProp } from '@ribajs/utils'
import type {
  iframeResizer,
  IFrameOptions,
  IFrameComponent,
} from 'iframe-resizer'

/**
 * Automatic resizing of the height and width of both same and cross domain iFrames to fit their contained content.
 * This binder can be used directly on the iframe element or on a parent element of the iframe(s) you want to resize.
 * @see https://github.com/davidjbradshaw/iframe-resizer
 */
export class IframeResizerBinder extends Binder<
  IFrameOptions,
  HTMLElement | HTMLIFrameElement
> {
  static key = 'iframe-resizer'

  iframeResizer?: typeof iframeResizer

  async bind() {
    if (!window.iFrameResize) {
      // TODO fix in @types/iframe-resizer

      // @ts-ignore
      await import('iframe-resizer/js/iframeResizer.js')
    }

    // Wait until iframe-resizer is finally imported
    await waitForProp('iFrameResize', window)

    if (!window.iFrameResize) {
      throw new TypeError(
        '[IframeResizerBinder] Module "iframe-resizer" not set!',
      )
    }
  }

  async routine(
    el: HTMLElement | HTMLIFrameElement,
    options: IFrameOptions = {},
  ) {
    // Wait until iframe-resizer is finally imported
    await waitForProp('iFrameResize', window)

    options.log ||= window.debug

    const iframes: Array<IFrameComponent | HTMLIFrameElement> | null =
      this.isIframe(el) ? [el] : Array.from(el.querySelectorAll('iframe'))
    const iframeComponents: IFrameComponent[] = []

    if (!window.iFrameResize) {
      throw new TypeError(
        '[IframeResizerBinder] Module "iframe-resizer" not set!',
      )
    }

    if (!this.allIframe(iframes)) {
      throw new TypeError('[IframeResizerBinder] No iFrame element found!')
    }

    for (const iframe of iframes) {
      if ((iframe as IFrameComponent).iFrameResizer) {
        if (options.log) {
          console.warn(
            '[IframeResizerBinder] IFrame already initialized',
            iframe,
          )
        }
        iframeComponents.push(iframe as IFrameComponent)
        continue
      }
      iframeComponents.push(...window.iFrameResize(options, iframe))
    }
  }

  /** Test if all elements in an array are iframes */
  allIframe(elements: Array<HTMLElement | HTMLIFrameElement> | null) {
    let result = true
    if (!elements || !elements.length) return false
    for (const el of elements) {
      result = result && this.isIframe(el)
      if (!result) return false
    }
    return result
  }

  /** Tests if html element is an iframe */
  isIframe(
    el: HTMLElement | HTMLIFrameElement | null,
  ): el is HTMLIFrameElement {
    return !!el && (el instanceof HTMLIFrameElement || el.tagName === 'IFRAME')
  }
}
